<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.cfg_settings') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <oxd-text
      v-if="state === 'loading'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-form v-else :loading="saving" @submit-valid="onSave">
      <template v-for="group in groups" :key="group.key">
        <oxd-form-row>
          <oxd-text type="card-title">{{ $t(group.title) }}</oxd-text>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item v-for="field in group.fields" :key="field.key">
              <oxd-input-field
                v-if="field.type === 'bool'"
                v-model="form[group.key][field.key]"
                type="checkbox"
                :true-value="true"
                :false-value="false"
                :option-label="$t(field.label)"
                :disabled="!canWrite"
              />
              <oxd-input-field
                v-else
                v-model="form[group.key][field.key]"
                :label="$t(field.label)"
                :rules="fieldRules(field)"
                :disabled="!canWrite"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
      </template>
      <oxd-divider />
      <oxd-form-actions v-if="canWrite">
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('expensa.discard_changes')"
          :disabled="!changed || saving"
          @click="resetForm"
        />
        <oxd-button
          type="submit"
          display-type="secondary"
          :label="$t('general.save')"
          :disabled="!changed || saving"
        />
      </oxd-form-actions>
      <oxd-text v-else tag="p" class="expensa-empty">
        {{ $t('expensa.settings_read_only') }}
      </oxd-text>
    </oxd-form>
  </div>
</template>

<script>
import {expensaApi, errorMessage} from '../../util/api';
import {
  SETTING_GROUPS,
  invalidKey,
  parseSetting,
  settingsPatch,
  settingsToForm,
} from '../../util/configForms';

// Impostazioni: il form nasce dai gruppi di SettingsSchema (SETTING_GROUPS) e
// il salvataggio invia solo i campi cambiati. Solo l'Admin può scrivere.
export default {
  name: 'ExpensaSettingsSection',
  props: {
    canWrite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: 'loading',
      groups: SETTING_GROUPS,
      original: null,
      form: null,
      saving: false,
    };
  },
  computed: {
    changed() {
      if (!this.original || !this.form) return false;
      const {patch} = settingsPatch(this.original, this.form);
      return Object.keys(patch).length > 0;
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    fieldRules(field) {
      return [(v) => parseSetting(field, v).ok || this.$t(invalidKey(field))];
    },
    adopt(settings) {
      this.original = settings;
      this.form = settingsToForm(settings);
    },
    resetForm() {
      this.form = settingsToForm(this.original);
    },
    async load() {
      try {
        const {data} = await expensaApi('/settings').getAll();
        this.adopt(data.data);
        this.state = 'ready';
      } catch (e) {
        this.state = 'error';
      }
    },
    async onSave() {
      const {patch, invalid} = settingsPatch(this.original, this.form);
      if (invalid.length > 0 || Object.keys(patch).length === 0) return;
      this.saving = true;
      try {
        // La rotta non ha id: PUT sull'indirizzo base.
        const {data} = await expensaApi('/settings').request({
          method: 'PUT',
          data: patch,
        });
        this.adopt(data.data);
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_done'),
        });
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
